import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, FileText } from 'lucide-react';

export default function TermsPage() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen pt-24 pb-16" data-testid="terms-page">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Breadcrumb */}
        <div className="mb-8">
          <Link 
            to="/" 
            className="text-white/60 hover:text-gold transition-colors flex items-center gap-2 text-sm"
          >
            <ArrowLeft className="w-4 h-4" />
            Înapoi la pagina principală
          </Link>
        </div>

        {/* Header */}
        <div className="glass rounded-2xl p-8 mb-8">
          <div className="flex items-center gap-4 mb-4">
            <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-gold to-gold-hover flex items-center justify-center">
              <FileText className="w-7 h-7 text-navy" />
            </div>
            <div>
              <h1 className="font-heading font-bold text-2xl md:text-3xl text-white">
                Termeni și condiții de utilizare
              </h1>
              <p className="text-gold">VaAi.ro</p>
            </div>
          </div>
          <p className="text-white/60 text-sm">
            Ultima actualizare: februarie 2026 – <span className="text-gold font-semibold">SITE IN LUCRU</span>
          </p>
        </div>

        {/* Content */}
        <div className="glass rounded-2xl p-8 space-y-8">
          {/* Intro */}
          <p className="text-white/80 leading-relaxed">
            Prin accesarea și utilizarea în orice mod a platformei VaAi.ro (inclusiv prin bifarea căsuței de acceptare la înregistrare/abonare sau prin continuarea utilizării după orice modificare a Termenilor) acceptați în mod expres și necondiționat acești Termeni și Condiții („Termenii"). Dacă nu sunteți de acord cu acești Termeni, vă rugăm să nu utilizați Platforma.
          </p>

          {/* Section 1 */}
          <section>
            <h2 className="font-heading font-bold text-xl text-gold mb-4">
              1. Identitatea operatorului platformei
            </h2>
            <div className="text-white/80 leading-relaxed space-y-2">
              <p><strong className="text-white">Platforma VaAi.ro</strong> este operată de:</p>
              <div className="bg-white/5 rounded-lg p-4 mt-3">
                <p className="text-gold font-semibold mb-2">⚠️ Date în curs de actualizare</p>
                <p className="text-white/60 text-sm">
                  Datele de identificare ale operatorului (denumire, CUI, sediu, contact) vor fi afișate aici 
                  odată cu finalizarea înregistrării legale.
                </p>
                <p className="text-white/60 text-sm mt-2">
                  Pentru întrebări sau solicitări, contactați-ne la: <a href="mailto:contact@vaai.ro" className="text-gold hover:underline">contact@vaai.ro</a>
                </p>
              </div>
            </div>
          </section>

          {/* Section 2 */}
          <section>
            <h2 className="font-heading font-bold text-xl text-gold mb-4">
              2. Obiectul și natura serviciilor
            </h2>
            <p className="text-white/80 leading-relaxed mb-4">
              VaAi.ro este un portal online care:
            </p>
            <ul className="text-white/80 leading-relaxed space-y-2 ml-4">
              <li>– agregă și afișează oferte de vacanță (circuite, sejururi, city break etc.) preluate de la touroperatori și parteneri externi</li>
              <li>– oferă recomandări personalizate prin intermediul unui asistent AI</li>
              <li>– facilitează redirectionarea către site-urile touroperatorilor pentru vizualizarea detaliilor și efectuarea rezervărilor</li>
            </ul>
            <p className="text-white/80 leading-relaxed mt-4 mb-4">
              <strong className="text-white">Platforma NU:</strong>
            </p>
            <ul className="text-white/80 leading-relaxed space-y-2 ml-4">
              <li>– este agenție de turism autorizată;</li>
              <li>– organizează, comercializează sau oferă pachete turistice;</li>
              <li>– intermediază contracte de turism;</li>
              <li>– procesează rezervări, plăți sau date de card;</li>
              <li>– încheie contracte în numele utilizatorului sau al touroperatorului.</li>
            </ul>
            <p className="text-white/80 leading-relaxed mt-4">
              Toate rezervările și plățile se realizează exclusiv pe site-ul touroperatorului, sub condițiile și termenii acestuia.
            </p>
          </section>

          {/* Section 3 */}
          <section>
            <h2 className="font-heading font-bold text-xl text-gold mb-4">
              3. Informațiile afișate și limitarea răspunderii
            </h2>
            <p className="text-white/80 leading-relaxed mb-4">
              Informațiile (prețuri, disponibilitate, descrieri, imagini, programe) sunt preluate automat sau manual de la touroperatori și parteneri externi. Acestea pot conține erori, pot fi incomplete sau pot fi modificate fără înștiințare prealabilă.
            </p>
            <p className="text-white/80 leading-relaxed mb-4">
              <strong className="text-white">VaAi exclude orice răspundere pentru:</strong>
            </p>
            <ul className="text-white/80 leading-relaxed space-y-2 ml-4">
              <li>– acuratețea, actualitatea, completitudinea sau disponibilitatea informațiilor</li>
              <li>– modificări, anulări, overbooking, probleme cu rezervarea sau calitatea serviciilor turistice</li>
              <li>– viruși, malware sau alte probleme pe site-urile externe la care utilizatorul este redirecționat</li>
              <li>– întreruperi tehnice, erori de afișare sau imposibilitatea temporară de acces</li>
              <li>– orice prejudicii directe sau indirecte rezultate din utilizarea Platformei</li>
            </ul>
            <p className="text-white/80 leading-relaxed mt-4">
              Utilizatorul are obligația de a verifica totul pe site-ul touroperatorului înainte de a rezerva.
            </p>
          </section>

          {/* Section 4 */}
          <section>
            <h2 className="font-heading font-bold text-xl text-gold mb-4">
              4. Utilizarea platformei
            </h2>
            <p className="text-white/80 leading-relaxed mb-4">
              Utilizarea este permisă doar în scopuri personale, necomerciale și legale. Este interzisă:
            </p>
            <ul className="text-white/80 leading-relaxed space-y-2 ml-4">
              <li>– utilizarea automată (scraping, bot, crawler) pentru extragerea datelor;</li>
              <li>– antrenarea modelelor AI pe conținutul Platformei fără acord scris;</li>
              <li>– modificarea, copierea, distribuirea sau reproducerea conținutului fără acord scris;</li>
              <li>– introducerea de conținut ilegal, abuziv, discriminatoriu sau care încalcă drepturile terților;</li>
              <li>– orice acțiune care supraîncarcă sau compromite securitatea Platformei.</li>
            </ul>
            <p className="text-white/80 leading-relaxed mt-4">
              Încălcarea poate duce la blocare imediată a accesului și acțiuni legale.
            </p>
          </section>

          {/* Section 5 */}
          <section>
            <h2 className="font-heading font-bold text-xl text-gold mb-4">
              5. Proprietate intelectuală
            </h2>
            <p className="text-white/80 leading-relaxed">
              Întregul conținut al Platformei (design, logo, texte, recomandări AI, structură) este proprietatea VaAi sau a partenerilor săi și este protejat de legislația privind drepturile de autor, mărcile comerciale și proprietatea intelectuală. Utilizatorul nu dobândește niciun drept asupra conținutului prin simpla utilizare a Platformei.
            </p>
          </section>

          {/* Section 6 */}
          <section>
            <h2 className="font-heading font-bold text-xl text-gold mb-4">
              6. Abonamente și plăți
            </h2>
            <p className="text-white/80 leading-relaxed mb-4">
              Abonamentele oferă acces extins la funcțiile AI și alte beneficii premium. Plățile se procesează exclusiv prin furnizori externi (Stripe, PayPal etc.). VaAi nu stochează și nu procesează date de card – toate tranzacțiile sunt gestionate de procesatorul de plăți conform termenilor acestuia.
            </p>
            <p className="text-white/80 leading-relaxed mb-4">
              Toate prețurile sunt exprimate în EUR și includ TVA aplicabil.
            </p>
            <p className="text-white/80 leading-relaxed">
              Dreptul de retragere de 14 zile se aplică conform legislației consumatorilor, dar este pierdut dacă serviciul digital a fost accesat și utilizat cu acordul utilizatorului înainte de expirarea termenului (art. 16 lit. m din OUG nr. 34/2014).
            </p>
          </section>

          {/* Section 7 */}
          <section>
            <h2 className="font-heading font-bold text-xl text-gold mb-4">
              7. Modificări ale termenilor
            </h2>
            <p className="text-white/80 leading-relaxed mb-4">
              VaAi poate modifica unilateral acești Termeni. Modificările se publică pe Platformă cu indicarea datei ultimei actualizări. Continuarea utilizării Platformei după publicarea modificărilor constituie acceptarea noilor Termeni.
            </p>
            <p className="text-white/80 leading-relaxed">
              Modificările substanțiale vor fi notificate utilizatorilor înregistrați prin email sau în Platformă cu minim 15 zile înainte (dacă este posibil).
            </p>
          </section>

          {/* Section 8 */}
          <section>
            <h2 className="font-heading font-bold text-xl text-gold mb-4">
              8. Legea aplicabilă și soluționarea litigiilor
            </h2>
            <p className="text-white/80 leading-relaxed mb-4">
              Acești Termeni sunt guvernați de legislația României. Orice litigiu decurgând din sau în legătură cu acești Termeni va fi soluționat pe cale amiabilă, iar în caz de neînțelegere, de instanțele judecătorești competente din [orașul tău, ex: București].
            </p>
            <p className="text-white/80 leading-relaxed">
              Pentru consumatori se aplică și prevederile OUG nr. 34/2014 privind drepturile consumatorilor în contractele încheiate la distanță și Legea nr. 363/2007 privind combaterea practicilor incorecte ale comercianților.
            </p>
          </section>

          {/* Footer */}
          <div className="pt-8 border-t border-white/10 text-center">
            <p className="text-gold font-semibold">
              VaAi.ro – Vacanțe AI © 2026 Toate drepturile rezervate.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
